import { useContext, useEffect, useState } from 'react';
import {
  DefaultButton,
  Dropdown,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  Stack,
  Text,
} from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { getApiErrorMessage } from '@moko/utils';
import AppContext from '../AppContext';
import { currentTenantNameAtom } from '../../atoms/authAtoms';
import { successMessageAtom } from '../../atoms/messageBarAtoms';
import {
  getTenantSurcharges,
  saveTenantSurcharge,
  type IAutomaticSurchargeRule,
} from '../../services/automaticSurchargeService';
import { tenantSurchargesPath } from './pricingSettings';

const locationLabel = (rule: IAutomaticSurchargeRule) =>
  rule.countryCode
    ? [rule.countryCode, rule.stateCode].filter(Boolean).join(' / ')
    : 'All locations';

const TenantSurchargeForm = () => {
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const pricingItemId = Number.parseInt(query.get('id') ?? '');
  const validId = !Number.isNaN(pricingItemId) && pricingItemId > 0;
  const tenantName = useAtomValue(currentTenantNameAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const context = useContext(AppContext);
  const [rule, setRule] = useState<IAutomaticSurchargeRule>();
  const [override, setOverride] = useState<boolean | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    context.setSelectedTab('Pricing');
  }, []);

  useEffect(() => {
    if (!validId) {
      setError('This surcharge could not be found.');
      setLoaded(true);
      return;
    }
    const controller = new AbortController();
    setLoaded(false);
    setError(undefined);
    getTenantSurcharges(controller)
      .then((current) => {
        if (controller.signal.aborted) return;
        const match = current.find(
          (item) => item.pricingItemId === pricingItemId
        );
        if (!match) {
          setError('This surcharge could not be found.');
          setLoaded(true);
          return;
        }
        setRule(match);
        setOverride(match.tenantEnabled);
        setLoaded(true);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setError(getApiErrorMessage(e, 'Unable to load tenant surcharges.'));
        setLoaded(true);
      });
    return () => controller.abort();
  }, [pricingItemId, validId]);

  const back = () => navigate(tenantSurchargesPath);

  const save = async () => {
    if (!rule) return;
    const controller = new AbortController();
    setSaving(true);
    setError(undefined);
    try {
      await saveTenantSurcharge(controller, rule.pricingItemId, override);
      setSuccessMessage('Tenant surcharge saved.');
      navigate(tenantSurchargesPath);
    } catch (e) {
      if (!controller.signal.aborted)
        setError(getApiErrorMessage(e, 'Unable to save tenant surcharge.'));
      setSaving(false);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="xLarge">Edit tenant surcharge</Text>
        {!loaded && !error && <Spinner label="Loading tenant surcharge…" />}
        {error && (
          <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
        )}
        {loaded && !rule && <DefaultButton text="Cancel" onClick={back} />}
        {rule && (
          <>
            <Text>
              {rule.name} ({rule.code}){tenantName ? ` · ${tenantName}` : ''}
            </Text>
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text={saving ? 'Saving…' : 'Save'}
                disabled={saving || override === (rule.tenantEnabled ?? null)}
                onClick={() => {
                  void save();
                }}
              />
              <DefaultButton text="Cancel" disabled={saving} onClick={back} />
            </Stack>
            <Text>Global default: {rule.enabled ? 'Enabled' : 'Disabled'}</Text>
            <Text>
              Effective setting:{' '}
              {rule.effectiveEnabled ? 'Enabled' : 'Disabled'}
            </Text>
            <Text>Site condition: {locationLabel(rule)}</Text>
            <Dropdown
              label="Tenant setting"
              selectedKey={
                override === null
                  ? 'inherit'
                  : override
                  ? 'enabled'
                  : 'disabled'
              }
              disabled={saving}
              options={[
                { key: 'inherit', text: 'Use global setting' },
                { key: 'enabled', text: 'Enabled' },
                { key: 'disabled', text: 'Disabled' },
              ]}
              onChange={(_, option) => {
                if (!option) return;
                setOverride(
                  option.key === 'inherit' ? null : option.key === 'enabled'
                );
                setError(undefined);
              }}
            />
          </>
        )}
      </Stack>
    </div>
  );
};

export default TenantSurchargeForm;
